import { useState, useEffect, useRef } from 'react';
import { X, Phone, MessageSquare, PauseCircle, PlayCircle } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { formatDistanceToNow, addDays, format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface HistoryItem {
  id: string;
  created_at: string;
  action_type: string;
  status: string;
  draft_content: string;
  approved_content: string | null;
}

const TYPE_LABEL: Record<string, string> = {
  first_contact:         'Eerste contact',
  follow_up:             'Opvolging',
  quote_communication:   'Offerte',
  scheduling:            'Inplannen',
  complaint_response:    'Klacht',
  relationship_close:    'Afsluiten',
  general_communication: 'Algemeen',
};

const STATUS_COLOR: Record<string, string> = {
  queued:   'var(--text-muted)',
  approved: 'var(--success-text)',
  sent:     'var(--accent)',
  rejected: 'var(--danger-text)',
};

const MOCK_HISTORY: Record<string, HistoryItem[]> = {
  'client-marco': [
    { id: 'mh1', created_at: new Date(Date.now() - 86400000 * 3).toISOString(), action_type: 'first_contact',      status: 'sent',    draft_content: 'Goedemiddag Marco, bedankt voor uw aanvraag. Wij nemen zo snel mogelijk contact met u op om een afspraak in te plannen.', approved_content: null },
    { id: 'mh2', created_at: new Date(Date.now() - 86400000).toISOString(),     action_type: 'follow_up',          status: 'queued',  draft_content: 'Beste Marco, ik wilde even opvolgen naar aanleiding van ons eerdere bericht. Heeft u al een moment kunnen bekijken?', approved_content: null },
  ],
  'client-hans': [
    { id: 'hh1', created_at: new Date(Date.now() - 86400000 * 6).toISOString(), action_type: 'quote_communication', status: 'sent', draft_content: 'Beste Hans, in de bijlage vindt u de offerte voor uw project. Graag hoor ik of u nog vragen heeft.', approved_content: null },
    { id: 'hh2', created_at: new Date(Date.now() - 86400000 * 2).toISOString(), action_type: 'follow_up',           status: 'sent', draft_content: 'Beste Hans, heeft u de offerte kunnen bekijken? Ik sta klaar om uw vragen te beantwoorden.', approved_content: null },
  ],
  'client-sara': [
    { id: 'sh1', created_at: new Date(Date.now() - 86400000 * 1).toISOString(), action_type: 'scheduling', status: 'queued', draft_content: 'Goedemiddag Sara, zou volgende week dinsdag om 10:00 schikken voor een kennismakingsgesprek?', approved_content: null },
  ],
};

export interface ClientDrawerProps {
  clientId: string | null;
  clientName: string;
  partnerId: string | null;
  onClose: () => void;
}

export function ClientDrawer({ clientId, clientName, partnerId, onClose }: ClientDrawerProps) {
  const [notes, setNotes] = useState('');
  const [holdUntil, setHoldUntil] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const notesRef = useRef(notes);
  notesRef.current = notes;

  // Load contact data and history when drawer opens
  useEffect(() => {
    if (!clientId) return;
    setNotes('');
    setHoldUntil(null);
    setHistory([]);

    if (!isSupabaseConfigured || !supabase) {
      setHistory(MOCK_HISTORY[clientId] ?? []);
      return;
    }

    // Load contact notes + hold
    supabase
      .from('semmy_contacts')
      .select('notes, hold_until')
      .eq('client_id', clientId)
      .eq('partner_id', partnerId ?? '')
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setNotes(data.notes ?? '');
          setHoldUntil(data.hold_until ?? null);
        }
      });

    // Load conversation history
    setLoadingHistory(true);
    supabase
      .from('semmy_actions')
      .select('id, created_at, action_type, status, draft_content, approved_content')
      .eq('client_id', clientId)
      .eq('partner_id', partnerId ?? '')
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        setHistory(data ?? []);
        setLoadingHistory(false);
      });
  }, [clientId, partnerId]);

  const saveNotes = async () => {
    if (!clientId || !isSupabaseConfigured || !supabase) return;
    setSavingNotes(true);
    await supabase
      .from('semmy_contacts')
      .upsert({ client_id: clientId, partner_id: partnerId ?? '', notes: notesRef.current }, { onConflict: 'client_id,partner_id' });
    setSavingNotes(false);
  };

  const toggleHold = async () => {
    if (!clientId) return;
    const newHold = holdUntil ? null : addDays(new Date(), 7).toISOString();
    setHoldUntil(newHold);
    if (!isSupabaseConfigured || !supabase) return;
    await supabase
      .from('semmy_contacts')
      .upsert({ client_id: clientId, partner_id: partnerId ?? '', hold_until: newHold }, { onConflict: 'client_id,partner_id' });
  };

  const isHeld = holdUntil && new Date(holdUntil) > new Date();

  if (!clientId) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer panel — right side on desktop, bottom sheet on mobile */}
      <div
        className="fixed z-50 bottom-0 left-0 right-0 max-h-[85vh] rounded-t-2xl overflow-hidden flex flex-col
                   lg:bottom-auto lg:top-0 lg:left-auto lg:right-0 lg:h-full lg:w-[400px] lg:rounded-none lg:rounded-l-2xl"
        style={{ background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-primary)', borderTop: '1px solid var(--border-primary)' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 shrink-0"
          style={{ borderBottom: '1px solid var(--border-separator)' }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[13px] font-bold"
                style={{ background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--accent)' }}>
                {clientName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{clientName}</p>
                {clientId !== clientName && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Phone size={10} style={{ color: 'var(--text-muted)' }} />
                    <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{clientId}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Hold toggle */}
            <button
              onClick={toggleHold}
              className="flex items-center gap-1.5 mt-3 text-[12px] px-3 py-1.5 rounded-lg border transition-all"
              style={{
                background: isHeld ? 'var(--warning-bg)' : 'var(--bg-input)',
                color: isHeld ? 'var(--warning-text)' : 'var(--text-secondary)',
                borderColor: isHeld ? 'var(--warning-border)' : 'var(--border-primary)',
              }}
            >
              {isHeld ? <PauseCircle size={13} /> : <PlayCircle size={13} />}
              {isHeld
                ? `Gepauzeerd tot ${format(new Date(holdUntil!), 'd MMM', { locale: nl })}`
                : 'Klant pauzeren (7 dagen)'}
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md transition-all"
            style={{ color: 'var(--text-muted)', background: 'var(--bg-input)' }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto space-y-5 px-5 py-4">

          {/* Notes */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
              Notities
            </p>
            <div className="relative">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                onBlur={saveNotes}
                placeholder="Voeg een notitie toe over deze klant..."
                rows={3}
                className="w-full rounded-lg p-3 text-[13px] leading-relaxed resize-none outline-none"
                style={{
                  background: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-primary)',
                }}
              />
              {savingNotes && (
                <span className="absolute bottom-2 right-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  Opslaan...
                </span>
              )}
            </div>
          </div>

          {/* Conversation history */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <MessageSquare size={11} style={{ color: 'var(--text-muted)' }} />
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                Berichtenhistorie
              </p>
            </div>

            {loadingHistory ? (
              <div className="space-y-2">
                {[1, 2].map(i => <div key={i} className="skeleton h-14 rounded-lg" />)}
              </div>
            ) : history.length === 0 ? (
              <p className="text-[12px] py-4 text-center" style={{ color: 'var(--text-muted)' }}>
                Geen berichten gevonden
              </p>
            ) : (
              <div className="space-y-2">
                {history.map(item => (
                  <div key={item.id} className="rounded-lg p-3"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-separator)' }}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                        {TYPE_LABEL[item.action_type] ?? item.action_type}
                      </span>
                      <span className="text-[10px] font-medium" style={{ color: STATUS_COLOR[item.status] ?? 'var(--text-muted)' }}>
                        {item.status}
                      </span>
                    </div>
                    <p className="text-[12px] leading-snug line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                      {item.approved_content ?? item.draft_content}
                    </p>
                    <p className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                      {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: nl })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
